/**
 * THE CABINET'S TEXTS, IN FOURTEEN LANGUAGES (design 29, W5).
 *
 * Every text a screen shows lives in `locales/<lang>/<namespace>.json`, one namespace per area. English
 * is bundled and initialised synchronously on import, so a test that renders a screen reads the same
 * English it always did and needs no setup. Every other language is its own chunk, fetched the first
 * time it is chosen (`import.meta.glob` below); a key missing there falls back to English.
 *
 * How a screen uses it:
 *   const { t } = useTranslation('articles');
 *   t('editor.submit')                         -> "Submit for review"
 *   t('list.count', { count: 3 })              -> plural forms `list.count_one` / `_other` (and `_few`,
 *                                                 `_many` where the language has them)
 *   <Trans t={t} i18nKey="editor.help" components={{ link: <a href="..." /> }} />
 *
 * Import `useTranslation`, `Trans` and `i18n` from THIS module, never from `react-i18next` directly:
 * importing it is what initialises the catalogue. A text needed outside React (a constant, a helper
 * returning a string) is translated where it is shown, not when the module loads, so a language change
 * redraws it.
 */
import i18n from 'i18next';
import type { BackendModule, ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';

import application from './locales/en/application.json';
import articles from './locales/en/articles.json';
import auth from './locales/en/auth.json';
import cabinet from './locales/en/cabinet.json';
import calendar from './locales/en/calendar.json';
import dashboard from './locales/en/dashboard.json';
import engagement from './locales/en/engagement.json';
import payments from './locales/en/payments.json';
import profile from './locales/en/profile.json';
import shell from './locales/en/shell.json';
import support from './locales/en/support.json';
import ui from './locales/en/ui.json';
import video from './locales/en/video.json';
import { CABINET_LANGUAGES, DEFAULT_CABINET_LANGUAGE } from './languages';
import type { CabinetLanguage } from './languages';

export const ENGLISH_RESOURCES = {
  application,
  articles,
  auth,
  cabinet,
  calendar,
  dashboard,
  engagement,
  payments,
  profile,
  shell,
  support,
  ui,
  video,
} as const;

export type CabinetNamespace = keyof typeof ENGLISH_RESOURCES;

export const CABINET_NAMESPACES = Object.keys(ENGLISH_RESOURCES) as CabinetNamespace[];

// Every non-English file is a lazy chunk; a language or namespace with no file yet reads as empty and
// falls back to English rather than failing the screen.
const lazyLocales = import.meta.glob<{ default: Record<string, unknown> }>([
  './locales/*/*.json',
  '!./locales/en/*.json',
]);

const lazyBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read(language: string, namespace: string, callback: ReadCallback) {
    const load = lazyLocales[`./locales/${language}/${namespace}.json`];
    if (load === undefined) {
      callback(null, {});
      return;
    }
    load().then(
      (module) => callback(null, module.default),
      (error: unknown) => callback(error instanceof Error ? error : new Error(String(error)), null),
    );
  },
};

void i18n
  .use(lazyBackend)
  .use(initReactI18next)
  .init({
    lng: DEFAULT_CABINET_LANGUAGE,
    fallbackLng: DEFAULT_CABINET_LANGUAGE,
    supportedLngs: CABINET_LANGUAGES.map((language) => language.code),
    ns: CABINET_NAMESPACES,
    defaultNS: 'shell',
    resources: { en: ENGLISH_RESOURCES },
    partialBundledLanguages: true,
    initAsync: false,
    returnNull: false,
    // React escapes what it renders; escaping here as well would show `&amp;` to the author.
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

/** Switch every screen to `language`, loading its files first. Resolves once they are in. */
export async function changeCabinetLanguage(language: CabinetLanguage): Promise<void> {
  await i18n.changeLanguage(language);
}

export { i18n };
export { Trans, useTranslation } from 'react-i18next';
export {
  CABINET_LANGUAGES,
  DEFAULT_CABINET_LANGUAGE,
  cabinetLanguageOf,
  isCabinetLanguage,
} from './languages';
export type { CabinetLanguage } from './languages';
